import React, { useState, useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { loanService } from '../../services/loanService'
import { savingsService } from '../../services/savingsService'
import api from '../../services/api'
import Swal from 'sweetalert2'

function LoanApply() {
  const navigate = useNavigate()
  const [members, setMembers] = useState([])
  const [selectedMember, setSelectedMember] = useState(null)
  const memberSelectRef = useRef(null)
  const [formData, setFormData] = useState({
    nba: '',
    kodeJenis: 'REGULER',
    plafond: 0,
    bunga: 24,
    tenor: 12,
    tipeBunga: 'Efektif',
    agunanList: [],
    agunanSimpanan: false,
    overrideMaxLoan: false,
    keterangan: ''
  })
  const [calculation, setCalculation] = useState(null)
  const [loading, setLoading] = useState(false)
  const [calculating, setCalculating] = useState(false)
  const [coopProfile, setCoopProfile] = useState(null)
  const [memberSavings, setMemberSavings] = useState(0)

  useEffect(() => {
    fetchMembers()
    fetchCoopProfile()
  }, [])

  const fetchMembers = async () => {
    try {
      // Fetch active members without active loans for loan application
      const response = await api.get('/members?limit=1000&statusAktif=1&excludeActiveLoans=1')
      const membersData = response.data.data || []
      console.log('Eligible members for loan:', membersData.length)
      setMembers(membersData)
    } catch (error) {
      console.error('Error fetching members:', error)
    }
  }

  const fetchCoopProfile = async () => {
    try {
      const response = await api.get('/reports/profile-info')
      setCoopProfile(response.data.data?.coopProfile || response.data.data)
    } catch (error) {
      console.error('Error fetching coop profile:', error)
    }
  }

  const calculateLoan = async () => {
    if (!formData.plafond || !formData.bunga) return

    setCalculating(true)
    try {
      const response = await loanService.calculate({
        plafond: formData.plafond,
        bunga: formData.bunga,
        tenor: formData.tenor,
        tipeBunga: formData.tipeBunga || 'Efektif'
      })
      setCalculation(response.data.data)
    } catch (error) {
      console.error('Error calculating:', error)
    } finally {
      setCalculating(false)
    }
  }

  useEffect(() => {
    const timeout = setTimeout(() => {
      if (formData.plafond > 0) {
        calculateLoan()
      }
    }, 500)
    return () => clearTimeout(timeout)
  }, [formData.plafond, formData.bunga, formData.tenor, formData.tipeBunga])

  useEffect(() => {
    if (memberSelectRef.current && members.length > 0) {
      // Initialize Select2
      $(memberSelectRef.current).select2({
        placeholder: '-- Pilih Anggota --',
        allowClear: true,
        width: '100%'
      })

      // Handle change event
      $(memberSelectRef.current).on('change', (e) => {
        const nba = e.target.value
        const member = members.find(m => m.NBA === nba)
        setSelectedMember(member || null)
        setFormData({ ...formData, nba: nba || '' })
        fetchMemberSavings(nba)
      })

      // Cleanup
      return () => {
        if (memberSelectRef.current) {
          $(memberSelectRef.current).off('change').select2('destroy')
        }
      }
    }
  }, [members])

  const handleSubmit = async (e) => {
    e.preventDefault()

    // Validate max loan amount
    const maxLoan = calculateMaxLoan()
    if (formData.plafond > maxLoan && !formData.overrideMaxLoan) {
      Swal.fire({
        icon: 'warning',
        title: 'Validasi Gagal',
        html: `Plafond tidak boleh melebihi maksimal pinjaman (Rp ${maxLoan.toLocaleString('id-ID')}).<br><br>Centang "Override Maksimal Pinjaman" jika ingin melanjutkan.`,
        confirmButtonText: 'OK'
      })
      return
    }

    setLoading(true)

    try {
      const response = await loanService.apply(formData)
      if (response.data.data && response.data.data.noPinjaman) {
        Swal.fire({
          icon: 'success',
          title: 'Berhasil!',
          text: 'Pengajuan pinjaman berhasil dibuat',
          timer: 2000,
          showConfirmButton: false
        }).then(() => {
          navigate(`/loans/${response.data.data.noPinjaman}`)
        })
      } else {
        Swal.fire({
          icon: 'error',
          title: 'Gagal',
          text: 'Gagal mengajukan pinjaman: No response data'
        })
      }
    } catch (error) {
      Swal.fire({
        icon: 'error',
        title: 'Gagal',
        text: error.response?.data?.message || 'Gagal mengajukan pinjaman'
      })
    } finally {
      setLoading(false)
    }
  }

  const handlePrintSchedule = () => {
    if (!calculation || !calculation.jadwalAngsuran) return

    const printWindow = window.open('', '_blank')
    printWindow.document.write(`
      <html>
        <head>
          <title>Simulasi Jadwal Angsuran</title>
          <style>
            @page { margin: 15mm; }
            body { font-family: Arial, sans-serif; font-size: 12px; }
            .header { text-align: center; margin-bottom: 20px; }
            .header h3 { margin: 0; font-size: 16px; }
            .info { margin-bottom: 15px; }
            .info table { width: 100%; border-collapse: collapse; }
            .info td { padding: 3px 0; }
            .info .label { width: 150px; font-weight: bold; }
            table { width: 100%; border-collapse: collapse; }
            th, td { border: 1px solid #000; padding: 8px; text-align: right; }
            th { background-color: #f0f0f0; font-weight: bold; text-align: center; }
            td:first-child, th:first-child { text-align: center; }
            .footer { margin-top: 20px; font-size: 11px; }
            .sign { margin-top: 30px; display: flex; justify-content: space-between; }
            .sign-box { width: 200px; text-align: center; }
            .sign-line { border-top: 1px solid #000; margin-top: 50px; padding-top: 5px; }
          </style>
        </head>
        <body>
          <div class="header">
            <h3>${coopProfile?.NamaKoperasi || 'KOPERASI'}</h3>
            <p>${coopProfile?.Alamat || ''}</p>
            <h4 style="margin-top: 15px;">SIMULASI JADWAL ANGSURAN PINJAMAN</h4>
          </div>
          
          <div class="info">
            <table>
              <tr><td class="label">Nama Anggota</td><td>: ${selectedMember?.Nama || '-'}</td></tr>
              <tr><td class="label">NBA</td><td>: ${selectedMember?.NBA || '-'}</td></tr>
              <tr><td class="label">Plafond</td><td>: ${formatCurrency(calculation.plafond)}</td></tr>
              <tr><td class="label">Tipe Bunga</td><td>: ${calculation.tipeBunga}</td></tr>
              <tr><td class="label">Suku Bunga/Tahun</td><td>: ${calculation.bunga}%</td></tr>
              <tr><td class="label">Tenor</td><td>: ${calculation.tenor} Bulan</td></tr>
            </table>
          </div>
          
          <table>
            <thead>
              <tr>
                <th>No</th>
                <th>Angsuran Pokok</th>
                <th>Angsuran Bunga</th>
                <th>Total Angsuran</th>
                <th>Sisa Pokok</th>
              </tr>
            </thead>
            <tbody>
              ${calculation.jadwalAngsuran.map((item, idx) => `
                <tr>
                  <td>${item.angsuranKe}</td>
                  <td>${formatCurrency(item.pokok)}</td>
                  <td>${formatCurrency(item.bunga)}</td>
                  <td>${formatCurrency(item.total)}</td>
                  <td>${formatCurrency(item.sisaPokok)}</td>
                </tr>
              `).join('')}
            </tbody>
          </table>
          
          <div class="footer">
            <p><strong>Keterangan:</strong></p>
            <ul>
              <li>Simulasi ini merupakan perkiraan dan dapat berubah sesuai kebijakan koperasi</li>
              <li>Bunga dihitung berdasarkan sisa pokok per bulan (menurun)</li>
              <li>Pembayaran dapat dilakukan lebih cepat tanpa penalty</li>
            </ul>
          </div>
          
          <div class="sign">
            <div class="sign-box">
              <p>Calon Anggota</p>
              <div class="sign-line">${selectedMember?.Nama || ''}</div>
            </div>
            <div class="sign-box">
              <p>Petugas Koperasi</p>
              <div class="sign-line">..........................</div>
            </div>
          </div>
          
          <div style="text-align: center; margin-top: 30px; font-size: 10px;">
            Dicetak tanggal: ${new Date().toLocaleDateString('id-ID', { day: '2-digit', month: 'long', year: 'numeric' })}
          </div>
        </body>
      </html>
    `)
    
    printWindow.document.close()
    setTimeout(() => printWindow.print(), 500)
  }

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('id-ID', {
      style: 'currency',
      currency: 'IDR',
      minimumFractionDigits: 0
    }).format(value || 0)
  }

  const fetchMemberSavings = async (nba) => {
    if (!nba) {
      setMemberSavings(0)
      return
    }
    try {
      console.log('Fetching savings for NBA:', nba)
      const response = await savingsService.getAccounts(nba)
      console.log('Savings response:', response.data)
      const accounts = response.data?.data || []
      console.log('Accounts:', accounts)
      const total = accounts.reduce((sum, acc) => sum + (parseFloat(acc.Saldo) || 0), 0)
      console.log('Total savings:', total)
      setMemberSavings(total)
    } catch (error) {
      console.error('Error fetching member savings:', error)
      setMemberSavings(0)
    }
  }

  const calculateTotalAgunan = () => {
    const agunanListTotal = formData.agunanList.reduce((sum, agunan) => sum + (parseFloat(agunan.nilaiAgunan) || 0), 0)
    const agunanSimpananValue = formData.agunanSimpanan ? memberSavings : 0
    return agunanListTotal + agunanSimpananValue
  }

  const calculateMaxLoan = () => {
    return Math.floor(calculateTotalAgunan() * 0.85)
  }

  const addAgunan = () => {
    setFormData(prev => ({
      ...prev,
      agunanList: [...prev.agunanList, { jenisAgunan: '', nilaiAgunan: '', keterangan: '' }]
    }))
  }

  const removeAgunan = (index) => {
    setFormData(prev => ({
      ...prev,
      agunanList: prev.agunanList.filter((_, i) => i !== index)
    }))
  }

  const updateAgunan = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      agunanList: prev.agunanList.map((agunan, i) =>
        i === index ? { ...agunan, [field]: value } : agunan
      )
    }))
  }

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : type === 'number' ? parseFloat(value) || 0 : value
    }))
  }

  return (
    <div className="row">
      <div className="col-md-6">
        <div className="card">
          <div className="card-header">
            <h3 className="card-title">Pengajuan Pinjaman</h3>
          </div>
          <form onSubmit={handleSubmit}>
            <div className="card-body">
              <div className="form-group">
                <label>NBA Anggota *</label>
                <select
                  ref={memberSelectRef}
                  className="form-control"
                  value={formData.nba}
                  onChange={(e) => {
                    const nba = e.target.value
                    setFormData({ ...formData, nba })
                    fetchMemberSavings(nba)
                  }}
                  required
                >
                  <option value="">-- Pilih Anggota --</option>
                  {Array.isArray(members) && members.map(m => (
                    <option key={m.NBA} value={m.NBA}>
                      {m.NBA} - {m.Nama}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label>Bunga (% per tahun) *</label>
                <input
                  type="number"
                  className="form-control"
                  value={formData.bunga}
                  onChange={(e) => setFormData({ ...formData, bunga: parseFloat(e.target.value) || 0 })}
                  required
                  min="0"
                  max="100"
                  step="0.1"
                />
                <small className="text-muted">Bunga efektif per tahun</small>
              </div>

              <div className="form-group">
                <label>Plafond Pinjaman *</label>
                <input
                  type="number"
                  className="form-control"
                  value={formData.plafond}
                  onChange={(e) => setFormData({ ...formData, plafond: parseInt(e.target.value) || 0 })}
                  required
                  min="0"
                />
              </div>

              <div className="form-group">
                <label>Tenor (bulan) *</label>
                <input
                  type="number"
                  className="form-control"
                  value={formData.tenor}
                  onChange={(e) => setFormData({ ...formData, tenor: parseInt(e.target.value) || 0 })}
                  required
                  min="1"
                  max="360"
                />
              </div>

              <div className="form-group">
                <label><i className="fas fa-file-contract mr-1"></i>Daftar Agunan</label>
                {formData.agunanList.length === 0 && (
                  <div className="text-muted mb-2">Belum ada agunan. Klik "Tambah Agunan" untuk menambahkan.</div>
                )}
                {formData.agunanList.map((agunan, index) => (
                  <div key={index} className="card mb-2 border-primary">
                    <div className="card-header bg-light py-2 d-flex justify-content-between align-items-center">
                      <span className="font-weight-bold">Agunan #{index + 1}</span>
                      <button
                        type="button"
                        className="btn btn-danger btn-sm"
                        onClick={() => removeAgunan(index)}
                      >
                        <i className="fas fa-trash"></i> Hapus
                      </button>
                    </div>
                    <div className="card-body p-3">
                      <div className="row">
                        <div className="col-md-6 mb-2">
                          <label className="small text-muted">Jenis Agunan</label>
                          <input
                            type="text"
                            className="form-control"
                            placeholder="Sertifikat, BPKB, Invoice, dll"
                            value={agunan.jenisAgunan}
                            onChange={(e) => updateAgunan(index, 'jenisAgunan', e.target.value)}
                            required
                          />
                        </div>
                        <div className="col-md-6 mb-2">
                          <label className="small text-muted">Nilai Agunan (Rp)</label>
                          <input
                            type="number"
                            className="form-control"
                            placeholder="0"
                            value={agunan.nilaiAgunan}
                            onChange={(e) => updateAgunan(index, 'nilaiAgunan', parseFloat(e.target.value) || 0)}
                            required
                          />
                        </div>
                      </div>
                      <div className="row">
                        <div className="col-md-6 mb-2">
                          <label className="small text-muted">Detail / Keterangan</label>
                          <input
                            type="text"
                            className="form-control"
                            placeholder="No. Sertifikat, Alamat, Merek Kendaraan, dll"
                            value={agunan.keterangan}
                            onChange={(e) => updateAgunan(index, 'keterangan', e.target.value)}
                          />
                        </div>
                        <div className="col-md-6 mb-2">
                          <label className="small text-muted">Dokumen Agunan (PDF/JPG/PNG)</label>
                          <input
                            type="file"
                            className="form-control-file"
                            accept=".pdf,.jpg,.jpeg,.png"
                            onChange={(e) => updateAgunan(index, 'file', e.target.files[0])}
                          />
                          {agunan.file && (
                            <small className="text-success">
                              <i className="fas fa-check mr-1"></i>
                              {agunan.file.name}
                            </small>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
                <button
                  type="button"
                  className="btn btn-outline-primary"
                  onClick={addAgunan}
                >
                  <i className="fas fa-plus mr-1"></i> Tambah Agunan
                </button>
              </div>

              <div className="form-group">
                <div className="custom-control custom-checkbox">
                  <input
                    type="checkbox"
                    className="custom-control-input"
                    id="agunanSimpanan"
                    checked={formData.agunanSimpanan}
                    onChange={(e) => setFormData({ ...formData, agunanSimpanan: e.target.checked })}
                  />
                  <label className="custom-control-label" htmlFor="agunanSimpanan">
                    <i className="fas fa-piggy-bank mr-1"></i>
                    Gunakan Simpanan sebagai Agunan Tambahan
                    {formData.agunanSimpanan && memberSavings > 0 && (
                      <span className="text-success ml-2 font-weight-bold">({formatCurrency(memberSavings)})</span>
                    )}
                  </label>
                </div>
              </div>

              <div className="alert alert-info">
                <h6 className="mb-3"><i className="fas fa-calculator mr-2"></i>Ringkasan Agunan:</h6>
                <table className="table table-sm table-borderless mb-0">
                  <tbody>
                    {formData.agunanList.map((agunan, idx) => (
                      <tr key={idx}>
                        <td>{idx + 1}. {agunan.jenisAgunan || 'Agunan'}</td>
                        <td className="text-right">{formatCurrency(agunan.nilaiAgunan)}</td>
                      </tr>
                    ))}
                    {formData.agunanSimpanan && (
                      <tr>
                        <td><i className="fas fa-piggy-bank mr-1"></i> Simpanan</td>
                        <td className="text-right">{formatCurrency(memberSavings)}</td>
                      </tr>
                    )}
                    <tr className="border-top">
                      <td className="pt-2"><strong>Total Agunan</strong></td>
                      <td className="text-right pt-2"><strong>{formatCurrency(calculateTotalAgunan())}</strong></td>
                    </tr>
                    <tr className="table-success">
                      <td className="pt-2"><strong><i className="fas fa-hand-holding-usd mr-1"></i>Maksimal Pinjaman (85%)</strong></td>
                      <td className="text-right pt-2"><strong className="text-success h5">{formatCurrency(calculateMaxLoan())}</strong></td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <div className="form-group">
                <label>Keterangan</label>
                <textarea
                  className="form-control"
                  rows="3"
                  value={formData.keterangan}
                  onChange={(e) => setFormData({ ...formData, keterangan: e.target.value })}
                />
              </div>
            </div>
            <div className="card-footer">
              <button type="submit" className="btn btn-primary" disabled={loading}>
                {loading ? (
                  <>
                    <i className="fas fa-spinner fa-spin mr-2"></i>
                    Mengirim...
                  </>
                ) : (
                  <>
                    <i className="fas fa-paper-plane mr-2"></i>
                    Ajukan Pinjaman
                  </>
                )}
              </button>
              <button
                type="button"
                className="btn btn-default ml-2"
                onClick={() => navigate('/loans/reguler')}
              >
                Batal
              </button>
            </div>
          </form>
        </div>
      </div>

      <div className="col-md-6">
        <div className="card">
          <div className="card-header">
            <h3 className="card-title">Simulasi Perhitungan</h3>
          </div>
          <div className="card-body">
            {calculating ? (
              <div className="text-center py-3">
                <i className="fas fa-spinner fa-spin"></i>
                <p className="mt-2">Menghitung...</p>
              </div>
            ) : calculation ? (
              <div>
                <div className="row mb-3">
                  <div className="col-6">
                    <small className="text-muted">Angsuran Pokok</small>
                    <h5>{formatCurrency(calculation.angsuranPokok || calculation.jadwalAngsuran[0]?.pokok)}</h5>
                  </div>
                  <div className="col-6 text-right">
                    <small className="text-muted">Angsuran Bunga</small>
                    <h5>{formatCurrency(calculation.angsuranBunga || calculation.jadwalAngsuran[0]?.bunga)}</h5>
                  </div>
                </div>
                <div className="alert alert-success text-center">
                  <small className="text-muted">Total Angsuran per Bulan</small>
                  <h4>{formatCurrency(calculation.totalAngsuran || calculation.jadwalAngsuran[0]?.total)}</h4>
                </div>

                <div className="d-flex justify-content-between align-items-center mt-4 mb-2">
                  <h6 className="mb-0">Jadwal Angsuran ({formData.tenor} Bulan)</h6>
                  <button 
                    className="btn btn-sm btn-info"
                    onClick={handlePrintSchedule}
                    disabled={!calculation?.jadwalAngsuran?.length}
                  >
                    <i className="fas fa-print mr-1"></i> Cetak Jadwal
                  </button>
                </div>
                <div className="table-responsive" style={{ maxHeight: '400px', overflow: 'auto' }}>
                  <table className="table table-sm table-bordered">
                    <thead className="thead-light">
                      <tr>
                        <th>Ke</th>
                        <th>Pokok</th>
                        <th>Bunga</th>
                        <th>Total</th>
                        <th>Sisa</th>
                      </tr>
                    </thead>
                    <tbody>
                      {Array.isArray(calculation.jadwalAngsuran) && calculation.jadwalAngsuran.map((item) => (
                        <tr key={item.angsuranKe}>
                          <td>{item.angsuranKe}</td>
                          <td className="text-right">{formatCurrency(item.pokok)}</td>
                          <td className="text-right">{formatCurrency(item.bunga)}</td>
                          <td className="text-right">{formatCurrency(item.total)}</td>
                          <td className="text-right">{formatCurrency(item.sisaPokok)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            ) : (
              <div className="text-center py-5 text-muted">
                <i className="fas fa-calculator fa-3x mb-3"></i>
                <p>Masukkan plafond dan tenor untuk melihat simulasi</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

export default LoanApply
